import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import { categories, getShowsByCategory, shows } from '@/mocks/shows';
import type { Show } from '@/mocks/shows';

function Breadcrumb({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav aria-label="面包屑导航" className="w-full px-4 md:px-6 py-3">
      <ol className="flex items-center flex-wrap gap-1 text-xs text-foreground-500">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && (
              <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-arrow-right-s-line"></i>
              </span>
            )}
            {item.href ? (
              <Link to={item.href} className="hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                {item.label}
              </Link>
            ) : (
              <span className="text-foreground-700">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'tv-series');
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'year'>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug, currentPage]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setCurrentPage(1);
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const categoryShows = getShowsByCategory(activeSlug);

  const sortedShows = [...categoryShows].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'year') return b.year - a.year;
    return b.id - a.id;
  });

  const totalPages = Math.ceil(sortedShows.length / pageSize);
  const pagedShows = sortedShows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const categoryMetaTitle = activeCategory
    ? `${activeCategory.name} - 天狼影院${activeCategory.name}高清免费在线观看`
    : '分类 - 天狼影院';
  const categoryMetaDesc = activeCategory
    ? `天狼影院${activeCategory.name}频道提供海量${activeCategory.name}资源，高清免费在线观看，流畅不缓冲，每日更新最新${activeCategory.name}。`
    : '天狼影院分类频道，涵盖电视剧、电影、动漫、综艺、纪录片等多种类型。';

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: activeCategory?.name || '分类' },
          ]}
        />

        {/* Category header */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12" aria-label="分类页头">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {activeCategory?.name || '全部分类'}
              </h1>
              <p className="text-sm text-foreground-500">
                共 {categoryShows.length} 部{activeCategory?.name || '作品'}
              </p>
            </div>
          </div>
        </section>

        {/* Category tabs */}
        <section className="w-full px-4 md:px-6 pb-4" aria-label="分类切换">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Sort controls */}
        <section className="w-full px-4 md:px-6 pb-6" aria-label="排序">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-500 mr-1">排序：</span>
            {[
              { key: 'latest', label: '最新' },
              { key: 'rating', label: '评分最高' },
              { key: 'year', label: '年份最新' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => { setSortBy(opt.key as typeof sortBy); setCurrentPage(1); }}
                className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  sortBy === opt.key
                    ? 'bg-accent-100 text-accent-700 border border-accent-200'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-transparent'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        {/* Show grid */}
        <section className="w-full px-4 md:px-6 pb-8" aria-label="剧集列表">
          {pagedShows.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {pagedShows.map((show) => (
                <VideoCard key={show.id} show={show} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200 text-foreground-400">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </span>
              <p className="text-foreground-500">暂无相关内容</p>
            </div>
          )}
        </section>

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="w-full px-4 md:px-6 pb-14 flex items-center justify-center gap-2" aria-label="分页">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => { setCurrentPage(p); window.scrollTo(0, 0); }}
                className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  p === currentPage
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </nav>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: categoryMetaTitle,
            description: categoryMetaDesc,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tianlangcinema.com/' },
                { '@type': 'ListItem', position: 2, name: activeCategory?.name || '分类' },
              ],
            },
          }),
        }}
      />
    </>
  );
}